/**
 * Browse-page result card (Iter 67k).
 *
 * Wider, horizontal-friendly card built for the new split-screen layout.
 * Left column: large square photo with overlay chips (car_type, host logo).
 * Right column: title, location, specs row (seats / transmission / fuel), price.
 * Hover scales the photo slightly + lifts the whole card with a subtle shadow.
 *
 * Forwards the current URL's search params to the detail page so the booking
 * widget arrives pre-filled (Iter 67b behaviour preserved).
 */
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Star, Users, Cog, Fuel, Snowflake, MapPin, Award, CreditCard } from "lucide-react";
import { matchFeature } from "@/components/car/featureCatalog";
import { fileUrl } from "@/lib/api";
import Money from "@/components/Money";
import useFeaturedHost from "@/lib/useFeaturedHost";
import CarCardBadges from "@/components/car/CarCardBadges";
import SocialProofBadge from "@/components/car/SocialProofBadge";
import { carTypeBadgeClass } from "@/lib/carTypeBadge";

const FALLBACK = "https://images.unsplash.com/photo-1576933765009-9f57f43910aa?auto=format&fit=crop&w=900&q=70";
const FORWARDED = ["pickup_location", "dropoff_location", "start_date", "end_date", "country"];

function buildLink(carId, search) {
  const incoming = new URLSearchParams(search || "");
  const out = new URLSearchParams();
  for (const k of FORWARDED) {
    const v = incoming.get(k);
    if (v) out.set(k, v);
  }
  const qs = out.toString();
  return qs ? `/cars/${carId}?${qs}` : `/cars/${carId}`;
}

export default function BrowseCarCard({ car, index = 0, onHover, proof = null }) {
  const firstImage = car.images?.[0];
  const src = firstImage ? (firstImage.startsWith("http") ? firstImage : fileUrl(firstImage)) : FALLBACK;
  const location = useLocation();
  const navigate = useNavigate();
  const href = buildLink(car.id, location.search);
  // Iter 70i — vendor deep-link handler. The outer card is a `<Link>` so we
  // intercept the click, stop propagation, and navigate programmatically to
  // `/vendors/{public_slug}` instead of nesting an anchor.
  const gotoVendor = (e) => {
    if (!car.vendor?.public_slug) return;
    e.preventDefault();
    e.stopPropagation();
    navigate(`/vendors/${car.vendor.public_slug}`);
  };
  // Iter 67af — pin a "Featured host" overlay on cars that belong to this
  // week's spotlighted vendor. Hook is shared (one fetch across all cards).
  const { vendorId: featuredVendorId } = useFeaturedHost();
  const isFeatured = featuredVendorId && car.vendor_id === featuredVendorId;

  return (
    <Link
      to={href}
      onMouseEnter={() => onHover?.(car.id)}
      onMouseLeave={() => onHover?.(null)}
      className="group flex flex-col sm:flex-row gap-4 rounded-3xl border border-border bg-white p-3 hover:shadow-xl hover:shadow-indigo-500/5 hover:border-indigo-100 hover:-translate-y-0.5 transition-all duration-300 fade-up"
      style={{ animationDelay: `${Math.min(index * 50, 600)}ms` }}
      data-testid={`car-card-${car.id}`}
    >
      <div className="relative shrink-0 sm:w-56 aspect-[4/3] rounded-2xl overflow-hidden bg-neutral-100">
        <img
          src={src}
          alt={`${car.make} ${car.model}`}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
          onError={(e) => { e.currentTarget.src = FALLBACK; }}
        />
        <div className="absolute top-2.5 left-2.5 flex flex-col gap-1.5 items-start">
          <div
            className={`backdrop-blur-sm rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${carTypeBadgeClass(car.car_type)}`}
            data-testid={`carcard-type-${car.id}`}
          >
            {car.car_type}
          </div>
          {isFeatured && (
            <div
              className="inline-flex items-center gap-1 bg-amber-400 text-amber-950 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em] shadow-md"
              data-testid={`carcard-featured-${car.id}`}
              title="Featured host of the week"
            >
              <Award className="h-2.5 w-2.5" /> Featured host
            </div>
          )}
          {/* Iter 67bp — Vendor-declared "no credit card" badge. Slashed icon
              + amber pill mirror the QuickFilterRail "No credit card" preset
              and the at-pickup payment chip on the detail page. */}
          {car.no_credit_card_required && (
            <div
              className="relative inline-flex items-center gap-1 bg-amber-400 text-amber-950 rounded-full pl-2 pr-2.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em] shadow-md"
              data-testid={`carcard-no-credit-card-${car.id}`}
              title="No credit card required — cash deposit accepted"
            >
              <span className="relative inline-flex h-3 w-3 items-center justify-center">
                <CreditCard className="h-2.5 w-2.5" />
                <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <span className="block h-[1.2px] w-3 bg-rose-600 rotate-45 origin-center" />
                </span>
              </span>
              No credit card
            </div>
          )}
        </div>
        {car.vendor?.business_logo && (
          <button
            type="button"
            onClick={gotoVendor}
            className="absolute top-2.5 right-2.5 h-8 w-8 rounded-full bg-white/95 backdrop-blur-sm border border-white/80 shadow-sm overflow-hidden flex items-center justify-center hover:scale-110 hover:shadow-md transition-transform"
            title={car.vendor?.business_name || car.vendor?.name || "Vendor"}
            data-testid={`carcard-logo-${car.id}`}
          >
            <img
              src={car.vendor.business_logo.startsWith("http") ? car.vendor.business_logo : fileUrl(car.vendor.business_logo)}
              alt={car.vendor?.business_name || "Vendor"}
              className="h-full w-full object-contain"
            />
          </button>
        )}
        {/* Iter 69 — social-proof badge at the bottom-left of the photo.
            Either "Booked Nm ago" (REAL, last 48h confirmed booking) or
            "X watching now" (pseudo-deterministic 2-8). Falls back to
            null when neither signal applies, so the photo stays clean. */}
        {proof && (
          <div className="absolute bottom-2.5 left-2.5 z-10" data-testid={`carcard-social-proof-${car.id}`}>
            <SocialProofBadge proof={proof} size="sm" />
          </div>
        )}
      </div>

      <div className="flex-1 min-w-0 flex flex-col py-2 pr-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-display text-lg font-medium text-neutral-900 truncate">
              {car.make} {car.model} <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-50 ring-1 ring-amber-200 text-amber-800 text-[10px] font-bold uppercase tracking-wider align-middle">or similar</span> <span className="text-neutral-400 font-normal text-base">· {car.year}</span>
            </div>
            <div className="text-xs text-neutral-500 truncate flex items-center gap-1 mt-0.5">
              <MapPin className="h-3 w-3 text-violet-500 shrink-0" />
              {car.location}
            </div>
            {/* Iter 74n — vendor name text hidden on card views per user request
                to reduce clutter. The clickable logo overlay above still
                routes to the vendor microsite. */}
          </div>
          <div className="flex items-center gap-1 text-sm shrink-0">
            <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
            <span className="font-semibold">{car.rating > 0 ? car.rating.toFixed(1) : "New"}</span>
            {car.review_count > 0 && <span className="text-neutral-400 text-xs">({car.review_count})</span>}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-3 text-xs text-neutral-700">
          <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5 text-sky-600" /> {car.seats}</span>
          <span className="inline-flex items-center gap-1"><Cog className="h-3.5 w-3.5 text-indigo-600" /> {car.transmission}</span>
          <span className="inline-flex items-center gap-1"><Fuel className="h-3.5 w-3.5 text-amber-600" /> {car.fuel_type}</span>
          {(car.features || []).some((f) => matchFeature(f)?.key === "ac") && (
            <span className="inline-flex items-center gap-1" data-testid={`carcard-ac-${car.id}`}>
              <Snowflake className="h-3.5 w-3.5 text-cyan-600" /> A/C
            </span>
          )}
        </div>

        <CarCardBadges
          car={car}
          className="mt-2"
          testIdPrefix={`carcard-perks-${car.id}`}
        />

        <div className="mt-auto pt-3 border-t border-neutral-100 mt-3 flex items-end justify-between gap-2">
          <div>
            {car.estimated_total ? (
              <>
                <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">Est. total</div>
                <div className="flex items-baseline gap-1">
                  <Money amount={car.estimated_total} className="font-display text-2xl font-bold text-neutral-900 tracking-tight" testId={`carcard-est-${car.id}`} />
                  <span className="text-xs text-neutral-400">· {car.estimated_days}d</span>
                </div>
              </>
            ) : (
              <div className="flex items-baseline gap-1">
                <Money amount={car.price_per_day} className="font-display text-2xl font-bold text-neutral-900 tracking-tight" testId={`carcard-price-${car.id}`} />
                <span className="text-xs text-neutral-500">/ day</span>
              </div>
            )}
          </div>
          <span className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full bg-indigo-600 text-white shadow-sm group-hover:bg-indigo-700 group-hover:shadow-md transition-all" data-testid={`carcard-view-${car.id}`}>
            View →
          </span>
        </div>
      </div>
    </Link>
  );
}
